import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Geofence } from '@rabbitfuel/shared';
import { formatKm, parseGpx, routeLength } from '../lib/gpx.ts';
import { useAppearance } from '../lib/appearance.ts';

const DEFAULT_CENTER: [number, number] = [53.5511, 9.9937];

/**
 * Zone auf einer OpenStreetMap-Karte festlegen.
 *
 * `point`: ein Klick setzt den Mittelpunkt, der Radius kommt vom Regler.
 * `route`: jeder Klick haengt einen Streckenpunkt an, der Radius ist die
 * halbe Korridorbreite links und rechts der Linie.
 */
export function GeofencePicker({
  value,
  onChange,
  hintCenter,
}: {
  value: Geofence | null;
  onChange: (next: Geofence | null) => void;
  /** Startausschnitt, wenn noch keine Zone existiert. */
  hintCenter?: [number, number];
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [locating, setLocating] = useState(false);
  const [importInfo, setImportInfo] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Zone und Strecke in der Hausfarbe dieses Browsers.
  const { brand } = useAppearance();

  /* Karte einmal aufbauen. */
  useEffect(() => {
    if (!holder.current || mapRef.current) return;
    const map = L.map(holder.current, { attributionControl: true }).setView(
      value?.points[0] ?? hintCenter ?? DEFAULT_CENTER,
      value ? 14 : 12,
    );
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      // Pflicht nach der Lizenz der Kartendaten (ODbL): Mitwirkende nennen, Lizenz verlinken.
      attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-Mitwirkende',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);

    map.on('click', (e: L.LeafletMouseEvent) => {
      const current = valueRef.current;
      const point: [number, number] = [e.latlng.lat, e.latlng.lng];
      if (!current) {
        onChange({ mode: 'point', enforcement: 'warn', radiusMeters: 500, points: [point] });
        return;
      }
      onChange(
        current.mode === 'point'
          ? { ...current, points: [point] }
          : { ...current, points: [...current.points, point] },
      );
    });

    mapRef.current = map;
    // Leaflet misst beim Aufbau oft zu frueh -- nach dem Layout korrigieren.
    setTimeout(() => {
      map.invalidateSize();
      // Eine gespeicherte Strecke ganz zeigen, nicht nur ihren Startpunkt --
      // eine Backyard-Runde ragt bei Zoom 14 sonst oben aus dem Bild. Genauso
      // rahmt der GPX-Import ein (siehe `importGpx`).
      const saved = valueRef.current;
      if (saved?.mode === 'route' && saved.points.length > 1) {
        map.fitBounds(L.latLngBounds(saved.points.map((p) => L.latLng(p[0], p[1]))), { padding: [24, 24] });
      }
    }, 60);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Zone neu zeichnen, wenn sie sich aendert. */
  useEffect(() => {
    const layer = layerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    if (!value || !value.points.length) return;

    const style = { color: brand, weight: 2, fillColor: brand, fillOpacity: 0.14 };

    if (value.mode === 'point') {
      L.circle(value.points[0], { radius: value.radiusMeters, ...style }).addTo(layer);
      L.circleMarker(value.points[0], { radius: 6, color: '#fff', weight: 2, fillColor: brand, fillOpacity: 1 }).addTo(layer);
    } else {
      // Korridor als dicke Linie: Breite in Metern, umgerechnet auf Pixel.
      const metersPerPixel =
        (40075016.686 * Math.abs(Math.cos((value.points[0][0] * Math.PI) / 180))) / 2 ** (map.getZoom() + 8);
      L.polyline(value.points, {
        color: brand,
        opacity: 0.2,
        weight: Math.max(6, (value.radiusMeters * 2) / metersPerPixel),
        lineCap: 'round',
        lineJoin: 'round',
      }).addTo(layer);
      L.polyline(value.points, { color: brand, weight: 3 }).addTo(layer);
      value.points.forEach((p, i) =>
        L.circleMarker(p, {
          radius: 5,
          color: '#fff',
          weight: 2,
          fillColor: i === 0 ? '#12855b' : brand,
          fillOpacity: 1,
        }).addTo(layer),
      );
    }
  }, [value, brand]);

  /* Korridorbreite haengt am Zoom -- beim Zoomen neu zeichnen. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const redraw = () => onChange({ ...(valueRef.current as Geofence) });
    if (value?.mode === 'route') {
      map.on('zoomend', redraw);
      return () => {
        map.off('zoomend', redraw);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.mode]);

  const setMode = (mode: 'point' | 'route') => {
    if (!value) {
      onChange({ mode, enforcement: 'warn', radiusMeters: mode === 'point' ? 500 : 250, points: [] });
      return;
    }
    // Beim Wechsel auf Punkt bleibt der erste Stützpunkt erhalten.
    onChange({ ...value, mode, points: mode === 'point' ? value.points.slice(0, 1) : value.points });
  };

  /** GPX aus Strava, Komoot oder Garmin einlesen und als Route uebernehmen. */
  const importGpx = async (file: File | undefined) => {
    if (!file) return;
    setImportError(null);
    setImportInfo(null);
    try {
      const result = parseGpx(await file.text());
      const radius = valueRef.current?.radiusMeters ?? 250;
      onChange({
        mode: 'route',
        enforcement: valueRef.current?.enforcement ?? 'warn',
        radiusMeters: radius,
        points: result.points,
      });
      // Karte auf die importierte Strecke einpassen.
      const map = mapRef.current;
      if (map && result.points.length > 1) {
        map.fitBounds(L.latLngBounds(result.points.map((p) => L.latLng(p[0], p[1]))), { padding: [24, 24] });
      }
      const shortened =
        result.originalCount > result.points.length
          ? ` · ${result.originalCount} Punkte auf ${result.points.length} vereinfacht`
          : ` · ${result.points.length} Punkte`;
      setImportInfo(`${result.name ?? file.name} — ${formatKm(result.lengthMeters)}${shortened}`);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import fehlgeschlagen.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        mapRef.current?.setView(here, 15);
        onChange(
          value
            ? { ...value, points: value.mode === 'point' ? [here] : [...value.points, here] }
            : { mode: 'point', enforcement: 'warn', radiusMeters: 500, points: [here] },
        );
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const ready = value && value.points.length >= (value.mode === 'route' ? 2 : 1);

  return (
    <div className="geopicker">
      <div className="geopicker__modes">
        <button
          type="button"
          className={`chip chip--light${value?.mode === 'point' ? ' is-active' : ''}`}
          onClick={() => setMode('point')}
        >
          📍 Punkt + Umkreis
        </button>
        <button
          type="button"
          className={`chip chip--light${value?.mode === 'route' ? ' is-active' : ''}`}
          onClick={() => setMode('route')}
        >
          〰️ Route + Korridor
        </button>
        <button type="button" className="chip chip--light" onClick={useMyLocation} disabled={locating}>
          {locating ? 'sucht …' : '🎯 Mein Standort'}
        </button>
        <button type="button" className="chip chip--light" onClick={() => fileRef.current?.click()}>
          📂 GPX importieren
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".gpx,application/gpx+xml,application/xml,text/xml"
          hidden
          onChange={(e) => void importGpx(e.target.files?.[0])}
        />
      </div>

      {importInfo && <div className="notice notice--ok">Strecke übernommen: {importInfo}</div>}
      {importError && <div className="notice">{importError}</div>}

      <div className="geopicker__map" ref={holder} />

      <p className="hint">
        {!value
          ? 'Wähle oben eine Art und klicke dann in die Karte.'
          : value.mode === 'point'
            ? 'In die Karte klicken setzt den Mittelpunkt.'
            : `Jeder Klick hängt einen Streckenpunkt an — ${value.points.length} gesetzt (mindestens 2 nötig).` +
              (value.points.length > 1 ? ` Länge: ${formatKm(routeLength(value.points))}.` : '')}
        {value?.mode === 'route' && ' Eine GPX-Datei aus Strava, Komoot oder Garmin geht auch.'}
      </p>

      {value && (
        <>
          <div className="field">
            <label htmlFor="radius">
              {value.mode === 'point' ? 'Umkreis' : 'Abstand ± zur Strecke'}: <b>{formatMeters(value.radiusMeters)}</b>
            </label>
            <input
              id="radius"
              type="range"
              min={50}
              max={5000}
              step={50}
              value={value.radiusMeters}
              onChange={(e) => onChange({ ...value, radiusMeters: Number(e.target.value) })}
            />
          </div>

          <div className="field">
            <label>Wenn jemand außerhalb steht</label>
            <div className="filters filters--light">
              <button
                type="button"
                className={value.enforcement === 'warn' ? 'is-active' : ''}
                onClick={() => onChange({ ...value, enforcement: 'warn' })}
              >
                Nur warnen
              </button>
              <button
                type="button"
                className={value.enforcement === 'block' ? 'is-active' : ''}
                onClick={() => onChange({ ...value, enforcement: 'block' })}
              >
                Blockieren
              </button>
            </div>
            <span className="hint">
              {value.enforcement === 'warn'
                ? 'Die Anfrage kommt trotzdem bei der Regie an, aber deutlich markiert. Gut für Hallen mit schlechtem GPS.'
                : 'Ohne bestätigten Standort in der Zone kommt keine Anfrage durch.'}
            </span>
          </div>

          <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
            {value.mode === 'route' && value.points.length > 0 && (
              <button
                type="button"
                className="btn btn--sm btn--quiet"
                onClick={() => onChange({ ...value, points: value.points.slice(0, -1) })}
              >
                Letzten Punkt zurück
              </button>
            )}
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => onChange(null)}>
              Zone entfernen
            </button>
            {!ready && <span className="hint">Zone noch unvollständig.</span>}
          </div>
        </>
      )}
    </div>
  );
}

export const formatMeters = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1).replace('.0', '')} km` : `${m} m`);
